/* Goal duration options, parsing, and formatting helpers. */

export interface GoalTimeOption {
  label: string;
  minutes: number;
}

export const GOAL_TIME_OPTIONS: GoalTimeOption[] = [
  { label: "30m", minutes: 30 },
  { label: "1h", minutes: 60 },
  { label: "1.5h", minutes: 90 },
  { label: "2h", minutes: 120 },
  { label: "2.5h", minutes: 150 },
  { label: "3h", minutes: 180 },
];

const MIN_GOAL_MINUTES = 5;
const MAX_GOAL_MINUTES = 480;

/** Format a minute count to a compact human label (e.g. 90 → "1.5h", 30 → "30m"). */
export function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = minutes / 60;
  const rounded = Math.round(hours * 10) / 10;
  return `${rounded}h`;
}

/**
 * Parse a user-typed time string into minutes.
 * Accepts: "30m", "1h", "1.5h", "90" (bare number = minutes).
 * Returns null if invalid.
 */
export function parseTimeInput(input: string): number | null {
  const trimmed = input.trim().toLowerCase();
  if (!trimmed) return null;

  const hourMatch = trimmed.match(/^(\d+\.?\d*)\s*h$/);
  if (hourMatch) {
    const hours = parseFloat(hourMatch[1]);
    if (isNaN(hours) || hours <= 0) return null;
    return Math.round(hours * 60);
  }

  const minMatch = trimmed.match(/^(\d+\.?\d*)\s*m$/);
  if (minMatch) {
    const mins = parseFloat(minMatch[1]);
    if (isNaN(mins) || mins <= 0) return null;
    return Math.round(mins);
  }

  const bare = parseFloat(trimmed);
  if (!isNaN(bare) && bare > 0 && /^\d+\.?\d*$/.test(trimmed)) {
    return Math.round(bare);
  }

  return null;
}

/** Validate parsed minutes, returning an error message or null if valid. */
export function validateGoalMinutes(minutes: number): string | null {
  if (minutes < MIN_GOAL_MINUTES) return `Minimum is ${MIN_GOAL_MINUTES} minutes`;
  if (minutes > MAX_GOAL_MINUTES) return `Maximum is ${MAX_GOAL_MINUTES / 60} hours`;
  return null;
}
